import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Clock, UserCheck, Trash2, RotateCcw } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TableRow, TableCell } from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { UnifiedInvitation } from "@/services/unifiedInvitationService";

export interface ProjectInvitationRowProps {
  invitation: UnifiedInvitation;
  onDelete: (invitationId: string) => void;
  onResend: (invitationId: string) => void;
  onDeleteProject: (projectId: string) => void;
  isDeletingInvitation: boolean;
  isResending: boolean;
  isDeletingProject: (projectId: string) => boolean;
}

const ProjectInvitationRow: React.FC<ProjectInvitationRowProps> = ({
  invitation,
  onDelete,
  onResend,
  onDeleteProject,
  isDeletingInvitation,
  isResending,
  isDeletingProject,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const isPending = !invitation.acceptedAt;
  const isExpired = invitation.expiresAt ? new Date(invitation.expiresAt) < new Date() : false;
  const isAccepted = !!invitation.acceptedAt;

  const getStatusBadge = () => {
    if (isAccepted) {
      return (
        <Badge
          variant="default"
          className="bg-green-100 text-green-800 hover:bg-green-200 h-6 px-2 text-xs uppercase tracking-wide shadow-sm"
        >
          <UserCheck className="h-3 w-3 mr-1" />
          Accepted
        </Badge>
      );
    }
    if (isExpired) {
      return (
        <Badge
          variant="outline"
          className="text-muted-foreground h-6 px-2 text-xs uppercase tracking-wide shadow-sm"
        >
          <Clock className="h-3 w-3 mr-1" />
          Expired
        </Badge>
      );
    }
    return (
      <Badge variant="secondary" className="h-6 px-2 text-xs uppercase tracking-wide shadow-sm">
        <Clock className="h-3 w-3 mr-1" />
        Pending
      </Badge>
    );
  };

  const invitedByName = invitation.invitedBy
    ? `${invitation.invitedBy.firstName || ''} ${invitation.invitedBy.lastName || ''}`.trim() ||
      invitation.invitedBy.email
    : 'Unknown';

  return (
    <>
      <TableRow>
        <TableCell className="font-medium">
          {invitation.email}
        </TableCell>
        <TableCell>
          <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="p-0 h-auto font-normal justify-start"
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 mr-2" />
                ) : (
                  <ChevronRight className="h-4 w-4 mr-2" />
                )}
                {invitation.projects.length} project{invitation.projects.length !== 1 ? 's' : ''}
              </Button>
            </CollapsibleTrigger>
          </Collapsible>
        </TableCell>
        <TableCell>
          <span className="text-muted-foreground">{invitedByName}</span>
        </TableCell>
        <TableCell>{getStatusBadge()}</TableCell>
        <TableCell className="text-muted-foreground">
          {invitation.createdAt ? new Date(invitation.createdAt).toLocaleDateString() : 'N/A'}
        </TableCell>
        <TableCell>
          {!isAccepted && (
            <div className="flex gap-1">
              {isExpired && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      disabled={isResending}
                      title="Resend invitation"
                    >
                      <RotateCcw className="h-4 w-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Resend Invitation</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to resend the invitation to{" "}
                        <strong>{invitation.email}</strong>? A new invitation email will be sent.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => onResend(invitation._id)}
                        className="bg-primary text-primary-foreground hover:bg-primary/90"
                      >
                        Resend
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
              {(isPending || isExpired) && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      disabled={isDeletingInvitation}
                      title="Delete invitation"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Invitation</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete the invitation for{" "}
                        <strong>{invitation.email}</strong>? This action cannot be undone and will remove
                        the invitation for all {invitation.projects.length} project{invitation.projects.length !== 1 ? 's' : ''}.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => onDelete(invitation._id)}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          )}
        </TableCell>
      </TableRow>

      {/* Expanded project details row */}
      <TableRow>
        <TableCell colSpan={6} className="p-0">
          <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
            <CollapsibleContent>
              <div className="px-6 py-4 bg-muted/30">
                <div className="space-y-3">
                  <h4 className="font-medium text-sm">Projects invited to:</h4>
                  <div className="space-y-2">
                    {invitation.projects.map((project, index) => (
                      <div
                        key={project.projectId._id}
                        className="flex items-center justify-between p-3 bg-background rounded-md border"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="flex-1">
                            <div className="font-medium text-sm">
                              {project.projectId.name}
                            </div>
                            {project.projectId.city && project.projectId.state && (
                              <div className="text-xs text-muted-foreground">
                                {project.projectId.city}, {project.projectId.state}
                              </div>
                            )}
                          </div>
                          <Badge variant="secondary" className="text-xs">
                            {project.role}
                          </Badge>
                        </div>
                        
                        {!isAccepted && invitation.projects.length > 1 && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                disabled={isDeletingProject(project.projectId._id)}
                                title="Remove from this project"
                              >
                                {isDeletingProject(project.projectId._id) ? (
                                  <RotateCcw className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Trash2 className="h-4 w-4" />
                                )}
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Remove Project from Invitation</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to remove <strong>{project.projectId.name}</strong> from the invitation
                                  for <strong>{invitation.email}</strong>? This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => onDeleteProject(project.projectId._id)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Remove
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </TableCell>
      </TableRow>
    </>
  );
};

export default ProjectInvitationRow;
